import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, FileText, Loader2, Sparkles } from "lucide-react";
import { API } from "@/constants";

const formatMarkdown = (text) => {
  if (!text || typeof text !== "string") return null;
  return text.split("\n").map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    // Headers
    if (line.startsWith("### ")) return <h4 key={i} className="text-sm font-bold mt-4 mb-1 text-slate-900">{line.slice(4)}</h4>;
    if (line.startsWith("## ")) return <h3 key={i} className="text-base font-bold mt-4 mb-1 text-slate-900">{line.slice(3)}</h3>;
    if (line.startsWith("# ")) return <h2 key={i} className="text-lg font-bold mt-4 mb-2 text-slate-900">{line.slice(2)}</h2>;
    // Bold headers like **TITLE**
    if (line.match(/^\*\*[^*]+\*\*$/)) {
      return <h4 key={i} className="text-sm font-bold mt-3 mb-1 text-slate-800">{line.replace(/\*\*/g, "")}</h4>;
    }
    // Bullet points
    if (line.match(/^[-*]\s/)) {
      const content = line.slice(2).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="text-sm text-slate-700 pl-4 py-0.5 border-l-2 border-blue-200" dangerouslySetInnerHTML={{ __html: content }} />;
    }
    // Numbered lists
    if (line.match(/^\d+\.\s/)) {
      const content = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="text-sm text-slate-700 pl-4 py-0.5" dangerouslySetInnerHTML={{ __html: content }} />;
    }
    // Regular paragraph with inline bold
    const content = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return <p key={i} className="text-sm text-slate-700 mb-1.5" dangerouslySetInnerHTML={{ __html: content }} />;
  });
};

const ReportViewPage = () => {
  const { id: reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/reports/${reportId}`, { withCredentials: true });
        setReport(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (reportId) fetchReport();
  }, [reportId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  if (!report) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="max-w-md">
        <CardContent className="pt-6 text-center">
          <p className="text-sm text-slate-500 mb-4">Report not found</p>
          <Link to="/reports"><Button>Back to Reports</Button></Link>
        </CardContent>
      </Card>
    </div>
  );

  const narrative = report.result?.analytical_narrative || report.result?.narrative || report.narrative || "";
  const findings = report.result?.findings || report.findings || [];
  const riskScore = report.result?.risk_score || report.risk_score;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="report-view">
      <div className="max-w-4xl mx-auto px-6 py-6">
        <Link to="/reports" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mb-4">
          <ChevronLeft className="h-4 w-4" /> Back to Reports
        </Link>

        <Card className="mb-4">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                {report.title || report.type || "Analysis Report"}
              </CardTitle>
              {riskScore && (
                <Badge className={`text-sm ${riskScore >= 70 ? "bg-red-500" : riskScore >= 40 ? "bg-amber-500" : "bg-green-500"}`}>
                  Risk: {riskScore}
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">{report.created_at?.split("T")[0] || ""}</p>
          </CardHeader>
        </Card>

        {/* Findings */}
        {findings.length > 0 && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base">Key Findings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {findings.map((f, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg border">
                    {typeof f === "string" ? (
                      <p className="text-sm">{f}</p>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium">{f.title || f.finding || ""}</p>
                          {f.severity && <Badge variant="outline" className="text-[10px]">{f.severity}</Badge>}
                        </div>
                        {f.description && <p className="text-xs text-slate-600">{f.description}</p>}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Narrative */}
        {narrative && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" /> Analytical Narrative
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none" data-testid="report-narrative">
                {formatMarkdown(typeof narrative === "string" ? narrative : JSON.stringify(narrative))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReportViewPage;
